import { useState } from 'react';
import { useAdminTenants, useUpdateTenant, useCreateTenant, useImpersonateTenant } from '@/hooks/useAdmin';
import { TenantWithSubscription, TenantWithAdminCreate } from '@/services/admin';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import {
  Building2,
  Search,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Calendar,
  Users,
  CreditCard,
  Power,
  Eye,
  Plus,
  Copy,
  AlertTriangle,
  LogIn,
  Store,
  Receipt,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

export default function AdminTenants() {
  const { data: tenants, isLoading } = useAdminTenants();
  const updateTenant = useUpdateTenant();
  const createTenant = useCreateTenant();
  const impersonateTenant = useImpersonateTenant();
  const startImpersonation = useAuthStore((state) => state.startImpersonation);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<TenantWithSubscription | null>(null);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTenant, setNewTenant] = useState<TenantWithAdminCreate>({
    tenant_name: '',
    tenant_slug: '',
    tenant_email: '',
    tenant_phone: '',
    tenant_address: '',
    admin_email: '',
    admin_full_name: '',
  });
  const [createdResult, setCreatedResult] = useState<{ password: string; email: string } | null>(null);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleCreateTenant = async () => {
    if (!newTenant.tenant_name || !newTenant.tenant_slug || !newTenant.admin_email || !newTenant.admin_full_name) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      // Limpiar campos vacíos para evitar errores de validación
      const dataToSend = {
        ...newTenant,
        tenant_email: newTenant.tenant_email?.trim() || null,
        tenant_phone: newTenant.tenant_phone?.trim() || null,
        tenant_address: newTenant.tenant_address?.trim() || null,
      };
      const result = await createTenant.mutateAsync(dataToSend);

      if (result.email_sent) {
        // Email enviado correctamente - cerrar modal
        toast.success(`Cliente creado. Se envió email de bienvenida a ${result.admin_email}`);
        setShowCreateModal(false);
        setNewTenant({
          tenant_name: '',
          tenant_slug: '',
          tenant_email: '',
          tenant_phone: '',
          tenant_address: '',
          admin_email: '',
          admin_full_name: '',
        });
      } else {
        // Email NO enviado - mostrar credenciales en el modal (NO cerrar)
        setCreatedResult({
          password: result.temporary_password || 'Error: contraseña no disponible',
          email: result.admin_email,
        });
        toast('Cliente creado, pero el email no pudo enviarse.', {
          icon: '⚠️',
          style: { background: '#fef3c7', color: '#92400e' },
        });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Error al crear el cliente');
    }
  };

  const filteredTenants = tenants?.filter(
    (tenant) =>
      tenant.name.toLowerCase().includes(search.toLowerCase()) ||
      tenant.slug.toLowerCase().includes(search.toLowerCase()) ||
      tenant.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleActive = async () => {
    if (!selectedTenant) return;
    await updateTenant.mutateAsync({
      id: selectedTenant.id,
      data: { is_active: !selectedTenant.is_active },
    });
    setShowToggleModal(false);
    setSelectedTenant(null);
  };

  const handleImpersonate = async (tenant: TenantWithSubscription) => {
    if (!tenant.is_active) {
      toast.error('No se puede acceder a un cliente inactivo');
      return;
    }
    try {
      const result = await impersonateTenant.mutateAsync(tenant.id);
      // Limpiar cache de React Query para que cargue datos del nuevo tenant
      queryClient.clear();
      startImpersonation(result.access_token, result.refresh_token, result.expires_in, result.tenant_name);
      toast.success(`Accediendo a ${result.tenant_name}`);
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Error al acceder al cliente');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getPaymentStatusBadge = (status: string | undefined) => {
    switch (status) {
      case 'paid':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Pagado</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Pendiente</span>;
      case 'overdue':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Vencido</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Sin suscripción</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="text-gray-500">Gestiona los almacenes y clientes del sistema</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-64"
            />
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{tenants?.length || 0}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {tenants?.filter((t) => t.is_active).length || 0}
              </p>
              <p className="text-xs text-gray-500">Activas</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {tenants?.filter((t) => t.subscription?.payment_status === 'pending').length || 0}
              </p>
              <p className="text-xs text-gray-500">Pago Pendiente</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {tenants?.filter((t) => !t.is_active).length || 0}
              </p>
              <p className="text-xs text-gray-500">Inactivas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tenants List */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Cargando...</div>
        ) : filteredTenants && filteredTenants.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredTenants.map((tenant) => (
              <div
                key={tenant.id}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    tenant.is_active
                      ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
                      : 'bg-gray-300'
                  }`}>
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                          {!tenant.is_active && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                              Inactiva
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">/{tenant.slug}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getPaymentStatusBadge(tenant.subscription?.payment_status)}
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
                      {tenant.email && (
                        <div className="flex items-center gap-1.5">
                          <Mail className="h-4 w-4 text-gray-400" />
                          {tenant.email}
                        </div>
                      )}
                      {tenant.phone && (
                        <div className="flex items-center gap-1.5">
                          <Phone className="h-4 w-4 text-gray-400" />
                          {tenant.phone}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <Users className="h-4 w-4 text-gray-400" />
                        {tenant.user_count} usuarios
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        Desde {formatDate(tenant.created_at)}
                      </div>
                    </div>

                    {/* Subscription Info */}
                    {tenant.subscription && (
                      <div className="mt-3 flex items-center gap-4 text-sm">
                        <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md font-medium capitalize">
                          Plan {tenant.subscription.plan}
                        </span>
                        <span className="text-gray-600">
                          ${tenant.subscription.price_monthly}/mes
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateTenant.mutate({ id: tenant.id, data: { online_store_enabled: !tenant.online_store_enabled } })}
                      disabled={updateTenant.isPending}
                      className={`p-2 rounded-lg ${
                        tenant.online_store_enabled
                          ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                          : 'hover:bg-gray-100 text-gray-400'
                      }`}
                      title={tenant.online_store_enabled ? 'Tienda online ACTIVA (clic para desactivar)' : 'Tienda online inactiva (clic para activar)'}
                    >
                      <Store className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => updateTenant.mutate({ id: tenant.id, data: { fiscal_enabled: !tenant.fiscal_enabled } })}
                      disabled={updateTenant.isPending}
                      className={`p-2 rounded-lg ${
                        tenant.fiscal_enabled
                          ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                          : 'hover:bg-gray-100 text-gray-400'
                      }`}
                      title={tenant.fiscal_enabled ? 'Facturación fiscal ACTIVA (clic para desactivar)' : 'Facturación fiscal inactiva (clic para activar)'}
                    >
                      <Receipt className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleImpersonate(tenant)}
                      disabled={!tenant.is_active || impersonateTenant.isPending}
                      className={`p-2 rounded-lg ${
                        tenant.is_active
                          ? 'hover:bg-indigo-50 text-indigo-500 hover:text-indigo-600'
                          : 'text-gray-300 cursor-not-allowed'
                      }`}
                      title={tenant.is_active ? 'Acceder al almacén' : 'Almacén inactivo'}
                    >
                      <LogIn className="h-5 w-5" />
                    </button>
                    <Link
                      to={`/admin/users?tenant=${tenant.id}`}
                      className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                      title="Ver usuarios"
                    >
                      <Eye className="h-5 w-5" />
                    </Link>
                    <button
                      onClick={() => {
                        setSelectedTenant(tenant);
                        setShowToggleModal(true);
                      }}
                      className={`p-2 rounded-lg ${
                        tenant.is_active
                          ? 'hover:bg-red-50 text-red-500 hover:text-red-600'
                          : 'hover:bg-green-50 text-green-500 hover:text-green-600'
                      }`}
                      title={tenant.is_active ? 'Desactivar' : 'Activar'}
                    >
                      <Power className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No se encontraron almacenes</p>
            {search && (
              <p className="text-sm text-gray-400 mt-1">
                Intenta con otro término de búsqueda
              </p>
            )}
          </div>
        )}
      </div>

      {/* Toggle Active Modal */}
      <Modal
        isOpen={showToggleModal}
        onClose={() => {
          setShowToggleModal(false);
          setSelectedTenant(null);
        }}
        title={selectedTenant?.is_active ? 'Desactivar Almacén' : 'Activar Almacén'}
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            ¿Estás seguro de que deseas {selectedTenant?.is_active ? 'desactivar' : 'activar'}{' '}
            <strong>{selectedTenant?.name}</strong>?
          </p>

          {selectedTenant?.is_active ? (
            <div className="p-4 bg-amber-50 rounded-lg text-sm text-amber-800">
              <strong>Atención:</strong> Al desactivar este almacén, sus usuarios no podrán
              acceder al sistema hasta que lo vuelvas a activar.
            </div>
          ) : (
            <div className="p-4 bg-green-50 rounded-lg text-sm text-green-800">
              Al activar este almacén, sus usuarios podrán acceder nuevamente al sistema.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowToggleModal(false);
                setSelectedTenant(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleToggleActive}
              loading={updateTenant.isPending}
              className={
                selectedTenant?.is_active
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }
            >
              {selectedTenant?.is_active ? 'Desactivar' : 'Activar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Tenant Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Nuevo Cliente"
      >
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
            Al crear un cliente, se enviará un email de bienvenida al administrador con sus
            credenciales de acceso.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre del Cliente *
              </label>
              <input
                type="text"
                value={newTenant.tenant_name}
                onChange={(e) => {
                  setNewTenant({
                    ...newTenant,
                    tenant_name: e.target.value,
                    tenant_slug: generateSlug(e.target.value),
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Ej: Almacén Central o Distribuidora Norte"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Identificador (slug) *
              </label>
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">/</span>
                <input
                  type="text"
                  value={newTenant.tenant_slug}
                  onChange={(e) =>
                    setNewTenant({ ...newTenant, tenant_slug: generateSlug(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="almacen-central"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Este identificador debe ser único y no podrá cambiarse
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email del Cliente
              </label>
              <input
                type="email"
                value={newTenant.tenant_email || ''}
                onChange={(e) => setNewTenant({ ...newTenant, tenant_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="contacto@ejemplo.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
              <input
                type="text"
                value={newTenant.tenant_phone || ''}
                onChange={(e) => setNewTenant({ ...newTenant, tenant_phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="011-4567-8900"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
              <input
                type="text"
                value={newTenant.tenant_address || ''}
                onChange={(e) => setNewTenant({ ...newTenant, tenant_address: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Av. Ejemplo 1234, Ciudad"
              />
            </div>
          </div>

          <hr className="border-gray-200" />

          <h4 className="font-medium text-gray-900">Datos del Administrador</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre Completo del Admin *
              </label>
              <input
                type="text"
                value={newTenant.admin_full_name}
                onChange={(e) => setNewTenant({ ...newTenant, admin_full_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Juan Pérez"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email del Admin *
              </label>
              <input
                type="email"
                value={newTenant.admin_email}
                onChange={(e) => setNewTenant({ ...newTenant, admin_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="admin@almacen.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                A este email se enviarán las credenciales de acceso
              </p>
            </div>
          </div>

          {createdResult ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">El email no pudo enviarse</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Debes comunicar estas credenciales manualmente al administrador del almacén.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div>
                  <label className="text-xs text-gray-500">Email</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border text-sm">{createdResult.email}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(createdResult.email);
                        toast.success('Email copiado');
                      }}
                      className="p-2 hover:bg-gray-200 rounded"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Contraseña temporal</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                      {createdResult.password}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(createdResult.password);
                        toast.success('Contraseña copiada');
                      }}
                      className="p-2 hover:bg-gray-200 rounded"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    setCreatedResult(null);
                    setShowCreateModal(false);
                    setNewTenant({
                      tenant_name: '',
                      tenant_slug: '',
                      tenant_email: '',
                      tenant_phone: '',
                      tenant_address: '',
                      admin_email: '',
                      admin_full_name: '',
                    });
                  }}
                >
                  Entendido
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleCreateTenant}
                loading={createTenant.isPending}
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear Cliente
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
